/**
 * POS Helper — Business logic for POS Counter Sale
 */

import {
  collection, addDoc, getDocs, query, where, doc, getDoc, setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTransactions } from './transactionHelper';
import { activeFyId, inActiveFy, ACTIVE_STOCK_STATUSES, getBranchLocation } from './stockFlowHelper';
import { getCached } from './fyDataCache';
import { applyBankDiscountToLines } from './bankDiscountHelper';

// ─── Walk-in Customer ───────────────────────────────────────────────

/**
 * Find or create the permanent "Walk-in Customer" record + linked account.
 * Returns { customerId, customerName, accountId, accountCode }
 */
export const ensureWalkInCustomer = async (companyId, userId) => {
  // Use a deterministic doc ID so two concurrent POS tills can never create
  // two separate walk-in customers. setDoc with the fixed ID is a safe upsert.
  // NOTE: Firestore reserves any doc id matching __.*__ (double underscore
  // on both sides), so we use plain ALL-CAPS identifiers here.
  const WALKIN_ID = 'WALKIN_CUSTOMER';
  const WALKIN_ACCOUNT_ID = 'WALKIN_ACCOUNT';

  const customersRef = collection(db, 'companies', companyId, 'customers');
  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const walkinDocRef = doc(db, 'companies', companyId, 'customers', WALKIN_ID);
  const walkinAccountDocRef = doc(db, 'companies', companyId, 'accounts', WALKIN_ACCOUNT_ID);

  // Legacy path: if there's already a walk-in created the old way (random ID), prefer that
  // to avoid creating a duplicate under the deterministic ID.
  const legacyQ = query(customersRef, where('isWalkIn', '==', true));
  const legacySnap = await getDocs(legacyQ);
  if (!legacySnap.empty) {
    const existing = { id: legacySnap.docs[0].id, ...legacySnap.docs[0].data() };
    const account = await getWalkInAccount(companyId, existing.id);
    return {
      customerId: existing.id,
      customerName: existing.companyName || existing.customerName || 'Walk-in Customer',
      accountId: account?.id || null,
      accountCode: account?.code || null
    };
  }

  // Upsert the deterministic walk-in customer record
  await setDoc(walkinDocRef, {
    companyName: 'Walk-in Customer',
    isWalkIn: true,
    isActive: true,
    phone: '', email: '', address: '', city: '',
    creditLimit: 0, openingBalance: 0,
    createdAt: new Date().toISOString(),
    createdBy: userId
  }, { merge: true });

  // Ensure linked account under Trade Receivables (11201)
  let accountId = null;
  let accountCode = null;

  const existingAccSnap = await getDoc(walkinAccountDocRef);
  if (existingAccSnap.exists()) {
    accountId = existingAccSnap.id;
    accountCode = existingAccSnap.data().code || '11201999';
  } else {
    const parentQuery = query(accountsRef, where('code', '==', '11201'));
    const parentSnap = await getDocs(parentQuery);
    if (!parentSnap.empty) {
      const parent = { id: parentSnap.docs[0].id, ...parentSnap.docs[0].data() };
      await setDoc(walkinAccountDocRef, {
        code: '11201999',
        name: 'Walk-in Customer',
        level: 5,
        parentId: parent.id,
        parentCode: parent.code,
        accountType: 'Asset',
        isActive: true,
        linkedEntityType: 'customer',
        linkedEntityId: WALKIN_ID,
        createdAt: new Date().toISOString(),
        createdBy: userId
      }, { merge: true });
      accountId = WALKIN_ACCOUNT_ID;
      accountCode = '11201999';
    }
  }

  return {
    customerId: WALKIN_ID,
    customerName: 'Walk-in Customer',
    accountId,
    accountCode
  };
};

const getWalkInAccount = async (companyId, customerId) => {
  const accountsRef = collection(db, 'companies', companyId, 'accounts');
  const q = query(
    accountsRef,
    where('linkedEntityType', '==', 'customer'),
    where('linkedEntityId', '==', customerId)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
};

// ─── Line Calculations ─────────────────────────────────────────────

/**
 * Calculate a single POS line item.
 * @param {object} line - { quantity, unitPrice, discountPercent, discountAmount, taxRate, taxable }
 * @param {'exclusive'|'inclusive'} taxMode
 */
export const calculatePOSLine = (line, taxMode = 'exclusive') => {
  const qty = Number(line.quantity) || 0;
  let unitPrice = Number(line.unitPrice) || 0;
  const discountPercent = Number(line.discountPercent) || 0;
  const taxRate = Number(line.taxRate) || 0;
  const taxable = line.taxable !== false;

  let priceExclTax = unitPrice;
  let priceInclTax = unitPrice;

  if (taxable && taxRate > 0 && taxMode === 'inclusive') {
    // Price entered is inclusive of tax — back-calculate
    priceExclTax = unitPrice / (1 + taxRate / 100);
    priceInclTax = unitPrice;
  } else if (taxable && taxRate > 0) {
    priceExclTax = unitPrice;
    priceInclTax = unitPrice * (1 + taxRate / 100);
  }

  const grossAmount = qty * priceExclTax;

  // Discount
  let discountAmount = Number(line.discountAmount) || 0;
  if (discountPercent > 0 && !discountAmount) {
    discountAmount = grossAmount * (discountPercent / 100);
  }
  discountAmount = Math.round(discountAmount * 100) / 100;

  const netAmount = grossAmount - discountAmount;

  // Tax
  const taxAmount = taxable && taxRate > 0
    ? Math.round(netAmount * (taxRate / 100) * 100) / 100
    : 0;

  const lineTotal = Math.round((netAmount + taxAmount) * 100) / 100;

  return {
    ...line,
    priceExclTax: Math.round(priceExclTax * 100) / 100,
    grossAmount: Math.round(grossAmount * 100) / 100,
    discountAmount,
    netAmount: Math.round(netAmount * 100) / 100,
    taxAmount,
    lineTotal,
    totalCost: qty * (Number(line.unitCost) || 0)
  };
};

// ─── Return against a saved receipt ────────────────────────────────
//
// A saved POS line keeps its unit price WITHOUT tax (posFastWriteHelper /
// posOfflineSave save priceExclTax) and the line total the customer actually
// paid (tax-inclusive, after the line discount and any bank card discount).
// A return must refund that paid amount for the returned units — it must NOT
// run the saved (already tax-free) price through the tax-inclusive engine again.

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * What a return line loaded from a saved receipt needs to price itself.
 * @param {object} cartLine  - the cart line as loaded (after any bank-discount reshaping)
 * @param {object} savedLine - the line as saved on the original sale
 */
export const receiptReturnBasis = (cartLine, savedLine, payFactor = 1) => {
  const paid = savedLine?.lineTotal;
  const f = Number(payFactor);
  return {
    qty: Number(savedLine?.quantity) || 0,
    // Share of the line total actually charged (a bill-level discount on the
    // original sale lowers it below 1).
    payFactor: Number.isFinite(f) && f > 0 && f < 1 ? f : 1,
    // null = the saved line has no paid total (very old document) → priced from the price
    lineTotal: (paid !== undefined && paid !== null && paid !== '' && Number.isFinite(Number(paid)) && Number(paid) >= 0)
      ? Number(paid) : null,
    discount: Number(savedLine?.discountAmount) || 0,
    // The values as loaded — if the cashier edits price / discount / unit, the
    // line is priced from what is on screen instead.
    price: Number(cartLine?.unitPrice) || 0,
    discPct: Number(cartLine?.discountPercent) || 0,
    discAmt: Number(cartLine?.discountAmount) || 0,
    unit: String(cartLine?.unitCode || cartLine?.unitOfMeasure || ''),
    priceType: cartLine?.priceType
  };
};

/** Cart line + its receipt basis (kept under `_ret`, never saved on the document). */
export const withReturnBasis = (cartLine, savedLine, payFactor = 1) => ({ ...cartLine, _ret: receiptReturnBasis(cartLine, savedLine, payFactor) });

/**
 * Share of the line totals the customer actually paid on a saved receipt:
 * the bill total ÷ the sum of its line totals (below 1 when the bill had a
 * bill-level discount). 1 when it can't be worked out.
 */
export const receiptPayFactor = (doc) => {
  const lines = (doc && doc.lines) || [];
  const sum = lines.reduce((s, l) => s + (Number(l && l.lineTotal) || 0), 0);
  const total = Number(doc && doc.totalAmount);
  if (!(sum > 0) || !Number.isFinite(total) || total <= 0 || total >= sum - 0.005) return 1;
  return total / sum;
};

/**
 * Price one return line that came from a saved receipt. The refund is the paid
 * line total pro-rated to the returned quantity, with the GST split out at the
 * line's rate — the same rule the Exchange screen uses. A line whose price,
 * discount or unit was changed on screen (or that has no saved total) is priced
 * from its price, which on a receipt line is already WITHOUT tax.
 */
export const calculatePOSReturnLine = (line, taxMode = 'inclusive') => {
  const b = line._ret || {};
  const origQty = Number(b.qty) || 0;
  // A changed unit or price type reloads the price from the item card, which
  // is tax-inclusive like any normal POS line → price it the normal way.
  const masterPriced = String(line.unitCode || line.unitOfMeasure || '') !== String(b.unit || '')
    || (line.priceType !== undefined && line.priceType !== b.priceType);
  if (masterPriced) return calculatePOSLine(line, taxMode);
  const unchanged = origQty > 0 && b.lineTotal !== null && b.lineTotal !== undefined
    && (Number(line.unitPrice) || 0) === (Number(b.price) || 0)
    && (Number(line.discountPercent) || 0) === (Number(b.discPct) || 0)
    && (Number(line.discountAmount) || 0) === (Number(b.discAmt) || 0);
  // A price or discount typed on the return line (the screen shows the saved
  // price WITHOUT tax), or a very old receipt with no saved total → price it
  // from that tax-free price.
  if (!unchanged) return calculatePOSLine(line, 'exclusive');

  const qty = Number(line.quantity) || 0;
  const taxRate = Number(line.taxRate) || 0;
  const taxable = line.taxable !== false;
  const fullTotal = r2(qty * (Number(b.lineTotal) || 0) / origQty);
  const fullNet = taxable && taxRate > 0 ? r2(fullTotal / (1 + taxRate / 100)) : fullTotal;
  let taxAmount = r2(fullTotal - fullNet);
  // A bill-level discount on the original sale came off after tax: refund
  // what was paid, and reverse the GST that was charged.
  const f = Number(b.payFactor) > 0 && Number(b.payFactor) < 1 ? Number(b.payFactor) : 1;
  const lineTotal = r2(fullTotal * f);
  let netAmount = r2(lineTotal - taxAmount);
  if (netAmount < 0) { // never more GST than the refund itself
    netAmount = taxable && taxRate > 0 ? r2(lineTotal / (1 + taxRate / 100)) : lineTotal;
    taxAmount = r2(lineTotal - netAmount);
  }
  const discountAmount = r2(qty * (Number(b.discount) || 0) / origQty);
  return {
    ...line,
    priceExclTax: r2(line.unitPrice),
    grossAmount: r2(netAmount + discountAmount),
    discountAmount,
    netAmount,
    taxAmount,
    lineTotal,
    totalCost: qty * (Number(line.unitCost) || 0)
  };
};

/**
 * Calculate cart totals.
 * @param {object} [opts] - { receiptReturn: true } while a return against a saved
 *   receipt is on screen: lines loaded from that receipt (they carry `_ret`) are
 *   priced by calculatePOSReturnLine. Without it nothing below changes.
 */
export const calculatePOSTotals = (cartItems, taxMode = 'exclusive', headerDiscount = 0, bankDiscountSpec = null, opts = null) => {
  const receiptReturn = !!(opts && opts.receiptReturn);
  let calculated = cartItems.map(item => (receiptReturn && item && item._ret
    ? calculatePOSReturnLine(item, taxMode)
    : calculatePOSLine(item, taxMode)));
  // POS bank card discount (module pos_bank_discounts): spread across the lines
  // BEFORE GST. Without a spec nothing below changes.
  let bankDiscountAmount = 0;
  if (bankDiscountSpec) {
    const applied = applyBankDiscountToLines(calculated, bankDiscountSpec);
    calculated = applied.lines;
    bankDiscountAmount = applied.amount;
  }

  const subtotal = calculated.reduce((s, l) => s + l.grossAmount, 0);
  const lineDiscounts = calculated.reduce((s, l) => s + l.discountAmount, 0);
  const totalDiscount = lineDiscounts + (Number(headerDiscount) || 0);
  const taxAmount = calculated.reduce((s, l) => s + l.taxAmount, 0);
  const totalAmount = calculated.reduce((s, l) => s + l.lineTotal, 0) - (Number(headerDiscount) || 0);
  const totalCOGS = calculated.reduce((s, l) => s + l.totalCost, 0);

  return {
    lines: calculated,
    subtotal: Math.round(subtotal * 100) / 100,
    lineDiscounts: Math.round(lineDiscounts * 100) / 100,
    totalDiscount: Math.round(totalDiscount * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
    totalCOGS: Math.round(totalCOGS * 100) / 100,
    ...(bankDiscountSpec ? { bankDiscountAmount } : {})
  };
};

// ─── Save POS Transaction ──────────────────────────────────────────

// ─── Receipt Lookup ─────────────────────────────────────────────────

/**
 * Look up a POS receipt by document number.
 */
export const lookupPOSReceipt = async (companyId, receiptNumber) => {
  const transactions = await getTransactions(companyId, { tranTypeCode: 'PS' });
  const found = transactions.find(t =>
    t.docNumber === receiptNumber || t.docNumber.endsWith(receiptNumber)
  );
  if (!found) return null;
  return found;
};

// ─── Stock Balance Helper ───────────────────────────────────────────

/**
 * Get stock balances for all items in a single Firestore query.
 * Fetches all Posted stockFlow entries and aggregates client-side.
 */
export const getStockBalances = async (companyId, itemIds, branchId = null, locationId = null) => {
  const balances = {};
  if (!itemIds || itemIds.length === 0) return balances;

  // Initialize all items to 0
  itemIds.forEach(id => { balances[id] = 0; });

  try {
    // Count EVERY active stock status — not just 'Posted'. Using 'Posted' alone
    // silently ignored GRN receipts ('Received'), Paid/Partial sale invoices and
    // delivered DCs, so the POS badge could disagree with Inventory Balances.
    const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
    const q = query(stockFlowRef, where('status', 'in', ACTIVE_STOCK_STATUSES));
    const snapshot = await getDocs(q);

    // Aggregate client-side by itemId. When the POS branch has a specific stock
    // location, filter to THAT location (a branch may hold several locations —
    // e.g. a store + a warehouse — but the till only sells from one). Otherwise
    // fall back to the branch filter (single-location branches, or when no
    // location is configured).
    const branchFilter = branchId && branchId !== 'all' ? branchId : null;
    // The caller passes the location off the branch record held in the LOGIN
    // SESSION, which is stale if Branch Setup was edited after signing in. Look
    // it up fresh — the same Branch → Location rule the stock WRITE already
    // applies — so the badge can't drift from what a sale actually deducts.
    let locationFilter = locationId || null;
    if (!locationFilter && branchFilter) {
      try {
        const branchLoc = await getBranchLocation(companyId, branchFilter);
        if (branchLoc?.id) locationFilter = branchLoc.id;
      } catch { /* fall back to branch-wide */ }
    }
    // Only the ACTIVE financial year — a new year's opening stock already contains
    // last year's closing, so counting both would double every item.
    const fy = activeFyId();
    snapshot.docs.forEach(d => {
      const data = d.data();
      const itemId = data.itemId;
      if (!itemId || !(itemId in balances)) return;
      if (!inActiveFy(data, fy)) return;
      if (locationFilter) {
        if (data.locationId !== locationFilter) return;
      } else if (branchFilter && data.branchId !== branchFilter) {
        return;
      }

      if (data.direction === 'in') {
        balances[itemId] += data.quantity || 0;
      } else if (data.direction === 'out') {
        balances[itemId] -= data.quantity || 0;
      }
    });
  } catch (error) {
    console.error('Error fetching stock balances:', error);
  }

  return balances;
};

// ─── Persisted POS balance map (survives reload / new tab) ──────────────
//
// The in-memory cache below dies with the page. A cashier who reloads, or opens
// the till in a second tab, used to re-pay the WHOLE stockFlow scan. This layer
// keeps the computed per-item map in localStorage so a reload costs 0 reads.
//
// Freshness rules (do not weaken):
//  • One 30-minute window, stamped at the moment of the real Firestore scan.
//    A local sale PATCHES the map but never re-stamps `at`, so another till's
//    movements can never be hidden for longer than 30 minutes.
//  • Every local stock change (sale / return / exchange / void / delete, and the
//    revert when a background save fails) is applied to the stored copy through
//    applyPersistedStockDelta — the persisted copy therefore always equals the
//    number on the tile.
//  • Anything unexpected (parse error, quota, write failure) DELETES the stored
//    copy rather than keeping it: the worst case is one extra full scan, never a
//    stale number.
//  • Items absent from the stored map stay absent (never invented as 0), so the
//    grid simply shows no badge for them and the checkout availability check
//    falls back to a live per-item read for exactly those items.
const POS_BAL_PREFIX = 'amal.pos.stockbal.';
const POS_BAL_MAX_AGE_MS = 30 * 60 * 1000; // ignore anything older on read
const POS_BAL_MAX_CHARS = 3000000;         // ~3 MB guard against the 5 MB quota

const hasLocalStorage = () => {
  try { return typeof window !== 'undefined' && !!window.localStorage; }
  catch (_) { return false; }
};

/** Same scope as the in-memory key: company + active FY + branch + location. */
const posBalKey = (companyId, branchId, locationId) => {
  const branchFilter = branchId && branchId !== 'all' ? branchId : 'all';
  return `${POS_BAL_PREFIX}${companyId}.${activeFyId() || 'nofy'}.${branchFilter}.${locationId || 'auto'}`;
};

const readPersistedStockBalances = (companyId, branchId, locationId) => {
  if (!companyId || !hasLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(posBalKey(companyId, branchId, locationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.balances || typeof parsed.balances !== 'object') return null;
    if (!parsed.at || Date.now() - parsed.at >= POS_BAL_MAX_AGE_MS) return null; // too old → re-fetch
    return parsed.balances;
  } catch (_) {
    return null; // corrupt → behave as if nothing was stored
  }
};

const writePersistedStockBalances = (companyId, branchId, locationId, balances) => {
  if (!companyId || !hasLocalStorage()) return;
  const key = posBalKey(companyId, branchId, locationId);
  try {
    const payload = JSON.stringify({ v: 1, at: Date.now(), balances });
    if (payload.length > POS_BAL_MAX_CHARS) return; // huge catalogue — stay in-memory only
    // Single-slot on purpose: drop any other company / FY / branch copy so this
    // never grows unbounded (a device sells from one till scope at a time).
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(POS_BAL_PREFIX) && k !== key) window.localStorage.removeItem(k);
    }
    window.localStorage.setItem(key, payload);
  } catch (_) { /* quota / private mode — the in-memory cache still works */ }
};

/**
 * Keep the persisted map in step with a local stock change.
 * @param {object} deltas - { [itemId]: signedBaseQty } — BASE units, same scale
 *                          as the stored map (stockFlow.quantity is base).
 * Items not already in the stored map are skipped (unknown stays unknown).
 * The original `at` stamp is preserved so a busy till still re-scans every 30 min.
 */
export const applyPersistedStockDelta = (companyId, branchId, locationId, deltas) => {
  if (!companyId || !deltas || !hasLocalStorage()) return;
  const key = posBalKey(companyId, branchId, locationId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return;                       // nothing persisted → nothing to keep in step
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.balances) { window.localStorage.removeItem(key); return; }
    if (!parsed.at || Date.now() - parsed.at >= POS_BAL_MAX_AGE_MS) {
      window.localStorage.removeItem(key); // already expired → drop, don't patch
      return;
    }
    let touched = false;
    for (const [itemId, qty] of Object.entries(deltas)) {
      const d = Number(qty) || 0;
      if (!d) continue;
      if (parsed.balances[itemId] === undefined) continue; // never invent an item
      parsed.balances[itemId] = (Number(parsed.balances[itemId]) || 0) + d;
      touched = true;
    }
    if (!touched) return;
    window.localStorage.setItem(key, JSON.stringify(parsed)); // `at` deliberately unchanged
  } catch (_) {
    // Could not patch → the copy might now disagree with reality. Drop it.
    try { window.localStorage.removeItem(key); } catch (_) { /* noop */ }
  }
};

/**
 * Cached getStockBalances (Tier 4, Firebase cost). POS opens frequently and
 * getStockBalances scans the WHOLE active stockFlow collection — the single
 * biggest per-open POS read. Two layers now sit in front of it:
 *
 *   1. the shared in-memory `stockSummary|${companyId}` namespace (dies with the
 *      page, cleared by the same generateStockFlow / deleteStockFlow paths every
 *      other screen fires, plus POSScreen's own post-sale invalidate), and
 *   2. localStorage (survives reload / a second tab) — see above.
 *
 * Both use ONE 30-minute window, so a reload inside that window costs 0 reads.
 * The 5-minute in-memory TTL it replaced bought nothing once layer 2 exists: the
 * numbers served after a 5-minute expiry would be the identical stored map.
 *
 * Keyed by company + active FY + branch + location. The item COUNT is no longer
 * part of the key — adding or de-activating one item used to orphan the whole
 * cache and force a full re-scan; an item missing from the map now just gets no
 * badge (and a live per-item check at checkout).
 */
const POS_STOCK_TTL_MS = 30 * 60 * 1000;
export const getStockBalancesCached = async (companyId, itemIds, branchId = null, locationId = null) => {
  const branchFilter = branchId && branchId !== 'all' ? branchId : 'all';
  const key = `stockSummary|${companyId}|posbal|${activeFyId() || 'nofy'}|${branchFilter}|${locationId || 'auto'}`;
  return getCached(key, async () => {
    const persisted = readPersistedStockBalances(companyId, branchId, locationId);
    if (persisted) return persisted;
    const fresh = await getStockBalances(companyId, itemIds, branchId, locationId);
    writePersistedStockBalances(companyId, branchId, locationId, fresh);
    return fresh;
  }, POS_STOCK_TTL_MS);
};

export default {
  ensureWalkInCustomer,
  calculatePOSLine,
  calculatePOSReturnLine,
  receiptReturnBasis,
  withReturnBasis,
  calculatePOSTotals,
  lookupPOSReceipt,
  getStockBalances,
  getStockBalancesCached,
  applyPersistedStockDelta
};
